import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import blogs from "./blogData";
import {
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Button,
  Tabs,
  Tab,
  Pagination,
  Box,
  useMediaQuery,
  useTheme
} from "@mui/material";
import "./Blog.css";

// Enhanced category normalization with strict matching
const normalizeCategory = (str) => {
  if (!str) return "";
  return str.toString().toLowerCase().trim();
};

// Generate categories from ACTUAL unique values in blog data
const allCategories = [...new Set(blogs.map(blog => normalizeCategory(blog.category)))].sort();

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0); // Add refresh key
  const blogsPerPage = 9;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
    setPage(1);
    setRefreshKey(prev => prev + 1); // Force refresh
  };

  // STRICT filtering with exact matching
  const filteredBlogs = React.useMemo(() => {
    if (selectedCategory === "all") return blogs;
    
    return blogs.filter(blog => {
      return normalizeCategory(blog.category) === normalizeCategory(selectedCategory);
    });
  }, [selectedCategory, refreshKey]); // Include refreshKey in dependencies

  const displayedBlogs = filteredBlogs.slice(
    (page - 1) * blogsPerPage,
    page * blogsPerPage
  );

  // Reset to page 1 when category changes
  useEffect(() => {
    setPage(1);
  }, [selectedCategory]);

  // Force complete remount of blog cards when category changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" }); // Instant scroll for immediate feedback
  }, [selectedCategory, refreshKey]);

  return (
    <div key={refreshKey}> {/* Force refresh of entire component */}
      <Container className="blog-container" maxWidth="xl">
        <Typography variant="h4" className="blog-title" gutterBottom>
          Ignite ideas. Expand expertise. Explore possibilities.
        </Typography>

        {/* Desktop Tabs */}
        {!isMobile && (
          <Tabs
            value={selectedCategory}
            onChange={handleCategoryChange}
            variant="scrollable"
            scrollButtons="auto"
            className="category-tabs"
            TabIndicatorProps={{ style: { backgroundColor: "#ff6500" } }}
          >
            <Tab label="All" value="all" className="category-tab" />
            {allCategories.map((category) => (
              <Tab
                key={category}
                label={category.charAt(0).toUpperCase() + category.slice(1)}
                value={category}
                className="category-tab"
              />
            ))}
          </Tabs>
        )}

        {/* Mobile Select */}
        {isMobile && (
          <Box sx={{ minWidth: 120, mb: 3 }}>
            <select
              value={selectedCategory}
              onChange={(e) => handleCategoryChange(null, e.target.value)}
              className="mobile-category-select"
            >
              <option value="all">All</option>
              {allCategories.map((category) => (
                <option key={category} value={category}>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </option>
              ))}
            </select>
          </Box>
        )}

        {/* Blog Cards - Will completely remount when refreshKey changes */}
        <Grid container spacing={3} className="blog-grid">
          {displayedBlogs.length > 0 ? (
            displayedBlogs.map((blog) => (
              <Grid item xs={12} sm={6} md={4} key={`${blog.id}-${refreshKey}`}> {/* Unique key with refreshKey */}
                <Card className="blog-card">
                  <CardMedia
                    component="img"
                    height="200"
                    image={blog.image || "https://via.placeholder.com/250"}
                    alt={blog.title}
                    className="blog-image"
                  />
                  <CardContent className="blog-content">
                    <Typography variant="h5" component="h2" className="blog-card-title">
                      {blog.title}
                    </Typography>
                    <Box className="date-wrapper">
                      <Typography variant="body2" color="textSecondary" className="blog-card-date">
                        {blog.date}
                      </Typography>
                    </Box>
                    <Box className="card-bottom-spacer" sx={{ flexGrow: 1 }} />
                    <Button
                      component={Link}
                      to={`/blog/${blog.id}`}
                      variant="contained"
                      className="read-more-btn"
                      fullWidth
                      sx={{ mt: 2 }}
                    >
                      Read More
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Typography variant="h6" sx={{ margin: "50px auto" }}>
              No blogs found in this category.
            </Typography>
          )}
        </Grid>

        {/* Pagination */}
        {filteredBlogs.length > blogsPerPage && (
          <Box className="pagination-container">
            <Pagination
              count={Math.ceil(filteredBlogs.length / blogsPerPage)}
              page={page}
              onChange={(_, value) => setPage(value)}
              className="pagination"
              color="primary"
            />
          </Box>
        )}
      </Container>
    </div>
  );
};

export default Blog;